// @ts-strict-ignore
import React, { useMemo } from 'react'
import { isArray, isEmpty } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { Box, toast } from '@node-space/ui-components'
import { CustomerDocumentsList } from 'components/EmbeddedCustomers/CustomerDocumentsList'
import { useCreateCustomerDocumentLink } from 'hooks/mutations/useCreateCustomerDocumentLink'
import { openInNewTab } from 'utils/openInNewTab'
import {
  APPROVED_DOCUMENT_STATUSES,
  COMPANY_REQUIRED_DOCUMENTS,
  DECLINED_DOCUMENT_STATUSES,
} from '../helpers/constants'
import type {
  CustomerDocument,
  DocumentRequirement,
  RequiredDocument,
  VerificationFailure,
} from '../helpers/types'
import { useMergedDocuments } from '../hooks/useMergedDocuments'

const buildRequiredNameMap = (documents: RequiredDocument[], translate: (key: string) => string) =>
  documents?.reduce<Record<string, string>>((map, document) => {
    const typeKeys = isArray(document?.type) ? document?.type : [document?.type]
    const keys: string[] = [...(document?.subType ? [document?.subType] : []), ...typeKeys]
    const effectiveKeys = keys.length > 0 ? keys : [document?.nameKey]

    effectiveKeys.forEach(key => {
      map[key] = translate(`embeddedCustomers.requiredDocuments.${document?.nameKey}`)
    })
    return map
  }, {})

interface CustomerDocumentsProps {
  documents: CustomerDocument[]
  isCompany: boolean
  infoRequiredDocuments: DocumentRequirement[]
  verificationFailure: VerificationFailure[]
  hasPendingQuestionnaires?: boolean
}

export const CustomerDocuments = ({
  documents,
  isCompany,
  infoRequiredDocuments,
  verificationFailure,
  hasPendingQuestionnaires = false,
}: CustomerDocumentsProps) => {
  const { t } = useTranslation()

  const { mutate: createCustomerDocumentLink } = useCreateCustomerDocumentLink()

  const { mergedDocuments, getIndividualRequiredDocuments } = useMergedDocuments(
    documents,
    isCompany,
    infoRequiredDocuments
  )
  const individualRequiredDocuments = useMemo(
    () => getIndividualRequiredDocuments(infoRequiredDocuments),
    [getIndividualRequiredDocuments, infoRequiredDocuments]
  )

  const requiredDocName = useMemo(() => {
    const documents = isCompany ? COMPANY_REQUIRED_DOCUMENTS : individualRequiredDocuments
    return buildRequiredNameMap(documents, t)
  }, [individualRequiredDocuments, isCompany, t])

  const { approvedDocs, pendingDocs, declinedDocs } = useMemo(() => {
    const allDocuments = isEmpty(mergedDocuments) ? (documents ?? []) : mergedDocuments
    return {
      approvedDocs: allDocuments?.filter(document =>
        APPROVED_DOCUMENT_STATUSES.includes(document?.status)
      ),
      pendingDocs: allDocuments?.filter(
        document =>
          !APPROVED_DOCUMENT_STATUSES.includes(document?.status) &&
          !DECLINED_DOCUMENT_STATUSES.includes(document?.status) &&
          (!isCompany ||
            hasPendingQuestionnaires ||
            (document as { isUploaded?: boolean })?.isUploaded !== false)
      ),
      declinedDocs: allDocuments?.filter(document =>
        DECLINED_DOCUMENT_STATUSES.includes(document?.status)
      ),
    }
  }, [documents, mergedDocuments])

  const hasApprovedDocs = !isEmpty(approvedDocs)
  const hasPendingDocs = !isEmpty(pendingDocs)
  const hasDeclinedDocs = !isEmpty(declinedDocs)

  const handleDocumentLink = (document: CustomerDocument) => {
    createCustomerDocumentLink(document?.reference, {
      onSuccess: data => {
        if (data) {
          openInNewTab(data)
          toast({
            header: t('embeddedCustomers.documentDownloadToast.success'),
            variant: 'success',
            body: `${document?.name}`,
          })
        }
      },
      onError: () => {
        toast({
          header: t('embeddedCustomers.documentDownloadToast.error'),
          variant: 'error',
        })
      },
    })
  }

  return (
    <Box className="flex flex-col gap-6">
      <CustomerDocumentsList
        listDocuments={approvedDocs}
        sectionTitle={
          hasPendingDocs || hasDeclinedDocs
            ? t('embeddedCustomers.documentsSection.approved')
            : undefined
        }
        isCompany={isCompany}
        requiredDocName={requiredDocName}
        onDocumentLink={handleDocumentLink}
      />
      <CustomerDocumentsList
        listDocuments={pendingDocs}
        sectionTitle={
          hasApprovedDocs || hasDeclinedDocs
            ? t('embeddedCustomers.documentsSection.pending')
            : undefined
        }
        isCompany={isCompany}
        requiredDocName={requiredDocName}
        onDocumentLink={handleDocumentLink}
      />
      <CustomerDocumentsList
        listDocuments={declinedDocs}
        sectionTitle={t('embeddedCustomers.documentsSection.declined')}
        isCompany={isCompany}
        requiredDocName={requiredDocName}
        onDocumentLink={handleDocumentLink}
      />
      {!isCompany && !isEmpty(verificationFailure) && (
        <Box className="flex flex-col gap-2">
          <span className="text-body-semibold  text-foreground-subdued">
            {t('embeddedCustomers.documentsSection.verificationFailureReasons')}
          </span>
          <ul className="list-disc pl-6 text-body-regular text-foreground-subdued">
            {verificationFailure.map(reason => (
              <li key={reason.failureCode}>{reason.description || reason.failureCode}</li>
            ))}
          </ul>
        </Box>
      )}
    </Box>
  )
}
